import { TextField, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef, useCallback } from 'react';

interface KeywordSearchFieldProps {
  initialValue?: string;
  isSearching?: boolean;
  onSearch: (keyword: string) => void;
  onClear?: () => void;
}

export function KeywordSearchField({
  initialValue = '',
  isSearching,
  onSearch,
  onClear,
}: KeywordSearchFieldProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEmittedRef = useRef(initialValue);

  // Sync if parent resets the initial value (e.g. context restore)
  const prevInitialRef = useRef(initialValue);
  useEffect(() => {
    if (initialValue !== prevInitialRef.current) {
      prevInitialRef.current = initialValue;
      setValue(initialValue);
      lastEmittedRef.current = initialValue;
    }
  }, [initialValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        const trimmed = newValue.trim();
        if (trimmed !== lastEmittedRef.current.trim()) {
          lastEmittedRef.current = newValue;
          onSearch(newValue);
        }
      }, 400);
    },
    [onSearch]
  );

  const handleClear = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setValue('');
    lastEmittedRef.current = '';
    onClear?.();
  }, [onClear]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <TextField
      value={value}
      onChange={handleChange}
      fullWidth
      label={t('search.keyword.label')}
      placeholder={t('search.keyword.placeholder')}
      variant="outlined"
      sx={{ mb: 3 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color="action" />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            {isSearching ? (
              <CircularProgress size={20} />
            ) : value ? (
              <IconButton
                aria-label={t('search.keyword.clear')}
                onClick={handleClear}
                edge="end"
                size="small"
              >
                <ClearIcon />
              </IconButton>
            ) : null}
          </InputAdornment>
        ),
      }}
    />
  );
}
